import { useDialog } from '@globals/dialog/useDialogStore'
import theme, { colors } from '@globals/themes/defaultTheme'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import { FC, ReactNode } from 'react'

export const DRAWER_HEADER_ID = 'drawer-header'

const defaultPaperSx: SxProps = {
  borderRadius: '16px 16px 0 0',
}

export const drawerPaperStyles: SxProps = {
  zIndex: theme.zIndex.drawer + 1,
  '& .MuiDrawer-paper': defaultPaperSx,
}

export const ModalProps = {
  // Fix accessibility issue with aria-hidden on backdrop
  BackdropProps: {
    // Ensure backdrop doesn't interfere with focus management
    'aria-hidden': undefined,
  },
  // Ensure proper focus management
  disablePortal: false,
}

type BottomDrawerProps = {
  drawerKey: string
  children: ReactNode
  onClose?: () => void | boolean
  title?: ReactNode
  drawerStyles?: SxProps
  headerStyles?: SxProps
  actionButtonCount?: 0 | 1 | 2
  backgroundColor?: string
  hideCloseButton?: boolean
  variant?: 'temporary' | 'persistent'
  /** When true, clicking the backdrop will not close the drawer (e.g. when a child dialog is open). */
  disableBackdropClose?: boolean
}

const BottomDrawer: FC<BottomDrawerProps> = ({
  drawerKey,
  children,
  onClose,
  title,
  actionButtonCount = 0,
  drawerStyles,
  headerStyles,
  backgroundColor = colors.whiteDark,
  hideCloseButton = false,
  variant = 'temporary',
  disableBackdropClose = false,
}) => {
  const { closeDialog, isOpen } = useDialog(drawerKey)

  const handleOnClose = () => {
    const preventClose = onClose?.() === false
    if (!preventClose) {
      closeDialog()
    }
  }

  const handleDrawerClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' && disableBackdropClose) {
      return
    }
    handleOnClose()
  }

  const bottomPadding = (): number => {
    switch (actionButtonCount) {
      case 1:
        return 10
      case 2:
        return 14
      default:
        return 0
    }
  }

  const drawerStylesRecord = drawerStyles as Record<string, SxProps> | undefined
  const paperOverrides = drawerStylesRecord?.['& .MuiDrawer-paper']

  const paperSx: SxProps = {
    ...defaultPaperSx,
    ...paperOverrides,
  }

  const withoutPaperKey = (record: Record<string, SxProps>) =>
    Object.fromEntries(Object.entries(record).filter(([key]) => key !== '& .MuiDrawer-paper'))
  const drawerStylesRest = drawerStylesRecord ? withoutPaperKey(drawerStylesRecord) : {}
  const drawerSx: SxProps = {
    zIndex: theme.zIndex.drawer + 1,
    ...drawerStylesRest,
  }

  return (
    <Drawer
      anchor="bottom"
      variant={variant}
      open={isOpen}
      onClose={handleDrawerClose}
      sx={drawerSx}
      slotProps={{ paper: { sx: paperSx } }}
      ModalProps={ModalProps}>
      {!hideCloseButton && (
        <Button
          id={`${drawerKey}-close`}
          sx={{ position: 'absolute', right: 18, top: 15 }}
          onClick={handleOnClose}>
          <CloseOutlinedIcon />
        </Button>
      )}
      {!!title && (
        <Box sx={{ p: 2, ...headerStyles }} id={DRAWER_HEADER_ID}>
          <Typography variant="h3" mt={{ xs: 0, sm: 0 }} sx={{ mx: 6 }} textAlign="center">
            {title}
          </Typography>
        </Box>
      )}
      <Box px={2} pt={title ? 0 : 4} pb={bottomPadding()} sx={{ backgroundColor }}>
        {children}
      </Box>
    </Drawer>
  )
}

export default BottomDrawer
